import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import { Route } from 'react-router-dom';
import useSWR from 'swr';
import { ApiKey } from '../../../../../../../../typings/ApiKey.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { isUnauthorizedError } from '../../../../../client/Hookdeck';
import LINKS from '../../../../../configs/links';
import isEmpty from '../../../../../utils/isEmpty';
import { describeApiKeyScopes } from '../../../../common/ApiKeys/ApiKeyScopesFields';
import Notification from '../../../../common/base/Alert';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Container from '../../../../common/base/Container';
import Icon, { IconName } from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Skeleton from '../../../../common/base/Skeleton';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import CopyableField from '../../../../common/CopyableField';
import { useDialog } from '../../../../common/Dialog';
import DropdownMenu from '../../../../common/DropdownMenu';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import Search from '../../../../common/Search';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { useApiKeyActions } from '../../../../hooks/useApiKeyActions';
import { DashboardContext } from '../../DashboardContext';
import { ProjectApiKeysTable } from '../Organization/OrganizationApiKeys/ProjectApiKeysTable';
import UpdateProjectApiKeyModal from '../Organization/OrganizationApiKeys/UpdateProjectApiKeyModal';
import { AddTeamApiKeyModal } from './AddTeamApiKeyModal';
const signing_secret_delay_options = [{
  label: 'now',
  value: '0'
}, {
  label: 'in 1 hour',
  value: '3600'
}, {
  label: 'in 24 hours',
  value: '86400'
}];
const signing_secret_form_props = {
  validate: (values: any) => {
    if (!values.delay_sec) {
      return {
        delay_sec: 'Required'
      };
    }
    return {};
  },
  Fields: () => <Div flex={{
    justify: 'space-between',
    align: 'center'
  }} m={{
    t: 6,
    b: 4
  }}>
      <Text bold>Key expiration</Text>
      <SelectInput block name="delay_sec" placeholder={'Select...'} required m={0} w={{
      px: 140
    }} options={signing_secret_delay_options} />
    </Div>
};
const getSigningSecretDelayLabel = (delay_sec: number): string | undefined => {
  return signing_secret_delay_options.find(option => option.value === delay_sec.toString())?.label.replace('in ', '');
};

// Read-only row for an organization key that reaches this project — lifecycle
// stays in Organization settings.
function OrganizationKeyRow(t0) {
  const $ = _c(28);
  const {
    api_key,
    team_id
  } = t0;
  const [is_secret, setIsSecret] = useState(true);
  const effective_scopes = team_id && api_key.grants?.[team_id]?.scopes || api_key.scopes || undefined;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 4
    };
    t2 = {
      align: "center",
      gap: 3
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center",
      gap: 2
    };
    t4 = {
      px: 235
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  const t5 = api_key.label ?? "Untitled";
  let t6;
  if ($[4] !== t5) {
    t6 = <Text semi={true} size="s" ellipsis={true}>{t5}</Text>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== effective_scopes) {
    t7 = describeApiKeyScopes({
      scopes: effective_scopes
    });
    $[6] = effective_scopes;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = <Badge neutral={true} subtle={true}>{t7}</Badge>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t6 || $[11] !== t8) {
    t9 = <Div min_w={0} flex={t3} w={t4}>{t6}{t8}</Div>;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      align: "center",
      gap: 1,
      grow: true
    };
    t11 = {
      y: 2
    };
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      grow: true
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== api_key.key || $[17] !== is_secret) {
    t13 = <CopyableField mono={true} ghost={true} text_size="xs" flex={t12} value={api_key.key} secret={is_secret} reveal_on_hover={false} visible_values={10} />;
    $[16] = api_key.key;
    $[17] = is_secret;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = is_secret ? "view" : "dismiss";
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsSecret(_temp);
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t14) {
    t16 = <Button icon={t14} ghost={true} onClick={t15} />;
    $[20] = t14;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t13 || $[23] !== t16) {
    t17 = <Div min_w={0} flex={t10} p={t11}>{t13}{t16}</Div>;
    $[22] = t13;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t17 || $[26] !== t9) {
    t18 = <StyledCardSection p={t1} flex={t2}>{t9}{t17}</StyledCardSection>;
    $[25] = t17;
    $[26] = t9;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  return t18;
}

// Members can't see organization keys: the section stays, with its content locked.
function _temp(prev) {
  return !prev;
}
function LockedOrganizationKeyRow() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "center",
      gap: 3
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Div min_w={0} w={{
      px: 235
    }}><Text semi={true} size="s" muted={true}>•••••••••••</Text></Div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center",
      gap: 1,
      grow: true
    };
    t4 = {
      y: 2
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <StyledCardSection p={t0} data-testid="project-org-api-keys-locked-row"><Tooltip fill={true} tooltip="Only organization admins can view organization API keys." side="bottom"><Div flex={t1}>{t2}<Div min_w={0} flex={t3} p={t4}><CopyableField mono={true} ghost={true} disabled={true} text_size="xs" flex={{
              grow: true
            }} value="*********************************************************" /><Button icon="view" ghost={true} disabled={true} /></Div></Div></Tooltip></StyledCardSection>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
const TeamApiKeys: React.FC = () => {
  const $ = _c(147);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    organization_role,
    team
  } = useContext(DashboardContext);
  const is_organization_admin = organization_role === "owner" || organization_role === "admin";
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [signing_secret_alerts, setSigningSecretAlerts] = useState(t0);
  const [is_secret, setIsSecret] = useState(true);
  const [search_value, setSearchValue] = useState("");
  const [editing_api_key, setEditingApiKey] = useState(null);
  let t1;
  if ($[1] !== team) {
    t1 = team ? APIMethodKeys.teams.listApiKeys(team.id) : null;
    $[1] = team;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== HookdeckAPI.teams) {
    t2 = () => HookdeckAPI.teams.listApiKeys();
    $[3] = HookdeckAPI.teams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      shouldRetryOnError: _temp2
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: api_keys,
    error: api_keys_error,
    mutate: mutateApiKeys
  } = useSWR(t1, t2, t3);
  let t4;
  if ($[6] !== is_organization_admin || $[7] !== organization) {
    t4 = organization && is_organization_admin ? APIMethodKeys.organizations.listApiKeys(organization.id) : null;
    $[6] = is_organization_admin;
    $[7] = organization;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== HookdeckAPI.organizations) {
    t5 = () => HookdeckAPI.organizations.listApiKeys();
    $[9] = HookdeckAPI.organizations;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      shouldRetryOnError: _temp3
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    data: organization_api_keys,
    error: organization_api_keys_error
  } = useSWR(t4, t5, t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = APIMethodKeys.session.getCurrentSigningSecret();
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== HookdeckAPI.session) {
    t8 = () => HookdeckAPI.session.getCurrentSigningSecret();
    $[13] = HookdeckAPI.session;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      shouldRetryOnError: _temp4
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: signing_secret,
    error: signing_secret_error,
    mutate: mutateSigningSecret
  } = useSWR(t7, t8, t9);
  let t10;
  if ($[16] !== mutateApiKeys) {
    t10 = {
      managed_from: "project",
      onRolled: () => mutateApiKeys(),
      onDeleted: id => mutateApiKeys(keys => keys?.filter(key => key.id !== id), false)
    };
    $[16] = mutateApiKeys;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const {
    handleRollApiKey,
    handleDeleteApiKey
  } = useApiKeyActions(t10);
  let t11;
  if ($[18] !== HookdeckAPI.session || $[19] !== addToast || $[20] !== mutateSigningSecret) {
    t11 = (signing_secret_0, delay_sec) => {
      HookdeckAPI.session.rollSigningSecret(signing_secret_0.id, {
        delay_sec
      }).then(res => {
        setSigningSecretAlerts(prev => ({
          ...prev,
          [res.key]: delay_sec > 0 ? `Your signing secret was rolled, your old secret will remain active for ${getSigningSecretDelayLabel(delay_sec)}.` : "Your signing secret was rolled, your old secret is now inactive."
        }));
        mutateSigningSecret(res);
      }).catch(() => {
        addToast("error", "Failed to roll signing secret");
      });
    };
    $[18] = HookdeckAPI.session;
    $[19] = addToast;
    $[20] = mutateSigningSecret;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const rollSigningSecret = t11;
  const signing_secret_alert_key = signing_secret?.key ?? "signing_secret";
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      href: LINKS.product_docs.signature_verification,
      text: "Learn more about signature verification"
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== rollSigningSecret || $[24] !== showDialog || $[25] !== signing_secret) {
    t13 = [{
      label: "Roll Key",
      icon: "roll" as IconName,
      onClick: () => showDialog(values => rollSigningSecret(signing_secret, values.delay_sec), _temp5, {
        title: "Roll signing secret",
        message: <Text>By rotating your signing secret your current secret will no longer be able to verify the signature. We will generate a new secret for you to use. If you select a rollover period both your old and new secret will be active for the remainder of the window. Make sure your server is configured to verify two signatures, if not, the verification will fail but you will always be able to retry those events at a later time. See{" "}<Link href={LINKS.product_docs.signature_verification} target="_blank" primary={true}>signature verification</Link>{" "}and{" "}<Link href={LINKS.product_docs.request_retries} target="_blank" primary={true}>retries</Link>.</Text>,
        submit_label: "Roll Signing Secret",
        danger: true,
        cancel_label: "Cancel",
        form_props: signing_secret_form_props
      })
    }];
    $[23] = rollSigningSecret;
    $[24] = showDialog;
    $[25] = signing_secret;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== signing_secret_error) {
    t14 = isUnauthorizedError(signing_secret_error) && <Tooltip fill={true} tooltip="As a viewer, you don't have permission to view or copy the signing secret. Contact your project admin for further access." side="bottom"><CopyableField mono={true} disabled={true} value="***********************************************************************" /></Tooltip>;
    $[27] = signing_secret_error;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13 || $[30] !== t14) {
    t15 = {
      title: "Signing Secret",
      description: "Use your signing secret to verify Hookdeck\u2019s signature.",
      link: t12,
      options: t13,
      unauthorized: t14
    };
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const signing_secret_section = t15;
  let t16;
  if ($[32] !== signing_secret) {
    t16 = isEmpty(signing_secret);
    $[32] = signing_secret;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const signing_secret_loading = t16;
  let t17;
  if ($[34] !== api_keys_error) {
    t17 = isUnauthorizedError(api_keys_error);
    $[34] = api_keys_error;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const api_keys_unauthorized = t17;
  let t18;
  if ($[36] !== api_keys || $[37] !== search_value) {
    t18 = search_value ? (api_keys ?? []).filter(api_key => api_key.label?.toLowerCase().includes(search_value.toLowerCase()) || api_key.key?.toLowerCase().includes(search_value.toLowerCase())) : api_keys ?? [];
    $[36] = api_keys;
    $[37] = search_value;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const filtered_api_keys = t18;
  let T0;
  let T1;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[39] !== api_keys_unauthorized || $[40] !== editing_api_key || $[41] !== filtered_api_keys || $[42] !== handleDeleteApiKey || $[43] !== handleRollApiKey || $[44] !== is_organization_admin || $[45] !== mutateApiKeys || $[46] !== organization_api_keys || $[47] !== organization_api_keys_error || $[48] !== search_value || $[49] !== team) {
    let t29;
    if ($[62] !== team) {
      t29 = api_key_0 => {
        if (api_key_0.team_id !== null) {
          return false;
        }
        const grants = api_key_0.grants ?? {};
        if (team?.id && grants[team.id]) {
          return grants[team.id].scopes.length > 0;
        }
        return Object.keys(grants).length === 0 && (api_key_0.scopes?.length ?? 1) > 0;
      };
      $[62] = team;
      $[63] = t29;
    } else {
      t29 = $[63];
    }
    const org_keys_with_access = (organization_api_keys ?? []).filter(t29);
    T1 = Container;
    t24 = true;
    t25 = true;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = {
        b: 10
      };
      $[64] = t26;
    } else {
      t26 = $[64];
    }
    if ($[65] !== mutateApiKeys) {
      t27 = <Route path="/settings/project/secrets/add" render={() => <AddTeamApiKeyModal onApiKeyCreated={api_key_1 => {
        mutateApiKeys(keys_0 => [api_key_1, ...(keys_0 ?? [])], false);
      }} />} />;
      $[65] = mutateApiKeys;
      $[66] = t27;
    } else {
      t27 = $[66];
    }
    if ($[67] !== editing_api_key || $[68] !== mutateApiKeys) {
      t28 = editing_api_key && <UpdateProjectApiKeyModal api_key={editing_api_key} managed_from="project" onClose={() => setEditingApiKey(null)} onApiKeyUpdated={updated_api_key => {
        mutateApiKeys(keys_1 => keys_1?.map(key_0 => key_0.id === updated_api_key.id ? updated_api_key : key_0), false);
      }} />;
      $[67] = editing_api_key;
      $[68] = mutateApiKeys;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    T0 = Div;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = {
        b: 10
      };
      $[70] = t19;
    } else {
      t19 = $[70];
    }
    let t30;
    let t31;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        b: 4
      };
      t31 = {
        justify: "space-between",
        align: "center"
      };
      $[71] = t30;
      $[72] = t31;
    } else {
      t30 = $[71];
      t31 = $[72];
    }
    let t32;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <Div><Text bold={true} size="l" as="h2" m={{
          y: 0
        }}>Project API Keys</Text><Text muted={true}>Use your API Keys to query the Hookdeck Admin API.</Text></Div>;
      $[73] = t32;
    } else {
      t32 = $[73];
    }
    let t33;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = {
        align: "center",
        gap: 3
      };
      $[74] = t33;
    } else {
      t33 = $[74];
    }
    let t34;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = {
        px: 240
      };
      $[75] = t34;
    } else {
      t34 = $[75];
    }
    let t35;
    if ($[76] !== api_keys_unauthorized || $[77] !== search_value) {
      t35 = <Search value={search_value} onChange={setSearchValue} small={true} disabled={api_keys_unauthorized} placeholder="Filter by name..." w={t34} />;
      $[76] = api_keys_unauthorized;
      $[77] = search_value;
      $[78] = t35;
    } else {
      t35 = $[78];
    }
    let t36;
    if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <Button.Permission data-testid="project-api-key-create" role="admin" unauthorized_tooltip="Only project admins can create API keys." to={_temp6} icon="add_circle">New API Key</Button.Permission>;
      $[79] = t36;
    } else {
      t36 = $[79];
    }
    if ($[80] !== t35) {
      t20 = <Div m={t30} flex={t31}>{t32}<Div flex={t33}>{t35}{t36}</Div></Div>;
      $[80] = t35;
      $[81] = t20;
    } else {
      t20 = $[81];
    }
    const t37 = team?.id;
    if ($[82] !== api_keys_unauthorized || $[83] !== filtered_api_keys || $[84] !== handleDeleteApiKey || $[85] !== handleRollApiKey || $[86] !== t37) {
      t21 = <ProjectApiKeysTable api_keys={filtered_api_keys} team_id={t37} unauthorized={api_keys_unauthorized} unauthorized_tooltip="As a viewer, you don't have permission to view project API keys. Contact your project admin for further access." onEditKey={setEditingApiKey} onRollKey={handleRollApiKey} onDeleteKey={handleDeleteApiKey} />;
      $[82] = api_keys_unauthorized;
      $[83] = filtered_api_keys;
      $[84] = handleDeleteApiKey;
      $[85] = handleRollApiKey;
      $[86] = t37;
      $[87] = t21;
    } else {
      t21 = $[87];
    }
    let t38;
    let t39;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
      t38 = {
        align: "center",
        gap: 1
      };
      t39 = {
        t: 4
      };
      $[88] = t38;
      $[89] = t39;
    } else {
      t38 = $[88];
      t39 = $[89];
    }
    let t40;
    if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <Link neutral={true} href={LINKS.api_ref.root} target="_blank"><Text semi={true} muted={true}>Learn more about the API</Text></Link>;
      $[90] = t40;
    } else {
      t40 = $[90];
    }
    if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Div flex={t38} m={t39}>{t40}<Text muted={true}><Icon icon="link" /></Text></Div>;
      $[91] = t22;
    } else {
      t22 = $[91];
    }
    t23 = (!is_organization_admin || !isUnauthorizedError(organization_api_keys_error) && org_keys_with_access.length > 0) && <Div m={{
      t: 10
    }}><Div m={{
        b: 4
      }} flex={{
        justify: "space-between",
        align: "center"
      }}><Div><Text bold={true} size="l" as="h2" m={{
            y: 0
          }}>Organization API Keys</Text><Text muted={true}>Organization keys with access to this project. Manage them in{" "}<Link primary={true} to="/settings/organization/api-keys">Organization settings</Link>.</Text></Div></Div><StyledCard>{is_organization_admin ? org_keys_with_access.map(api_key_2 => <OrganizationKeyRow key={api_key_2.id} api_key={api_key_2} team_id={team?.id} />) : <LockedOrganizationKeyRow />}</StyledCard></Div>;
    $[39] = api_keys_unauthorized;
    $[40] = editing_api_key;
    $[41] = filtered_api_keys;
    $[42] = handleDeleteApiKey;
    $[43] = handleRollApiKey;
    $[44] = is_organization_admin;
    $[45] = mutateApiKeys;
    $[46] = organization_api_keys;
    $[47] = organization_api_keys_error;
    $[48] = search_value;
    $[49] = team;
    $[50] = T0;
    $[51] = T1;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    T0 = $[50];
    T1 = $[51];
    t19 = $[52];
    t20 = $[53];
    t21 = $[54];
    t22 = $[55];
    t23 = $[56];
    t24 = $[57];
    t25 = $[58];
    t26 = $[59];
    t27 = $[60];
    t28 = $[61];
  }
  let t29;
  if ($[92] !== T0 || $[93] !== t19 || $[94] !== t20 || $[95] !== t21 || $[96] !== t22 || $[97] !== t23) {
    t29 = <T0 m={t19}>{t20}{t21}{t22}{t23}</T0>;
    $[92] = T0;
    $[93] = t19;
    $[94] = t20;
    $[95] = t21;
    $[96] = t22;
    $[97] = t23;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] !== T1 || $[100] !== t24 || $[101] !== t25 || $[102] !== t26 || $[103] !== t27 || $[104] !== t28 || $[105] !== t29) {
    t30 = <T1 large={t24} left={t25} m={t26}>{t27}{t28}{t29}</T1>;
    $[99] = T1;
    $[100] = t24;
    $[101] = t25;
    $[102] = t26;
    $[103] = t27;
    $[104] = t28;
    $[105] = t29;
    $[106] = t30;
  } else {
    t30 = $[106];
  }
  let t31;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      b: 10
    };
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  let t32;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      b: 10
    };
    $[108] = t32;
  } else {
    t32 = $[108];
  }
  let t33;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      b: 4
    };
    $[109] = t33;
  } else {
    t33 = $[109];
  }
  let t34;
  if ($[110] !== signing_secret_section.title) {
    t34 = <Text bold={true} size="l" as="h2" m={0}>{signing_secret_section.title}</Text>;
    $[110] = signing_secret_section.title;
    $[111] = t34;
  } else {
    t34 = $[111];
  }
  let t35;
  if ($[112] !== signing_secret_section.description) {
    t35 = <Text muted={true}>{signing_secret_section.description}</Text>;
    $[112] = signing_secret_section.description;
    $[113] = t35;
  } else {
    t35 = $[113];
  }
  let t36;
  if ($[114] !== t34 || $[115] !== t35) {
    t36 = <Div m={t33}>{t34}{t35}</Div>;
    $[114] = t34;
    $[115] = t35;
    $[116] = t36;
  } else {
    t36 = $[116];
  }
  let t37;
  let t38;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      align: "center",
      gap: 3
    };
    t38 = {
      b: 4
    };
    $[117] = t37;
    $[118] = t38;
  } else {
    t37 = $[117];
    t38 = $[118];
  }
  let t39;
  if ($[119] !== is_secret || $[120] !== signing_secret?.key || $[121] !== signing_secret_loading || $[122] !== signing_secret_section.options || $[123] !== signing_secret_section.unauthorized) {
    t39 = signing_secret_section.unauthorized ? signing_secret_section.unauthorized : signing_secret_loading ? <><Skeleton large={true} flex={{
        grow: true
      }} /><Skeleton large={true} variant="square" /></> : <><CopyableField mono={true} flex={{
        grow: true
      }} value={signing_secret?.key as string} secret={is_secret} reveal_on_hover={false} /><Button icon={is_secret ? "view" : "dismiss"} large={true} outline={true} onClick={() => setIsSecret(_temp7)} /><DropdownMenu placement="bottom-end" w={{
        px: 192
      }} renderToggle={_temp8} options={signing_secret_section.options} /></>;
    $[119] = is_secret;
    $[120] = signing_secret?.key;
    $[121] = signing_secret_loading;
    $[122] = signing_secret_section.options;
    $[123] = signing_secret_section.unauthorized;
    $[124] = t39;
  } else {
    t39 = $[124];
  }
  let t40;
  if ($[125] !== t39) {
    t40 = <Div flex={t37} m={t38}>{t39}</Div>;
    $[125] = t39;
    $[126] = t40;
  } else {
    t40 = $[126];
  }
  let t41;
  if ($[127] !== signing_secret_alert_key || $[128] !== signing_secret_alerts) {
    t41 = signing_secret_alerts[signing_secret_alert_key] && <Notification inline={true} info={true} m={{
      b: 4
    }}>{signing_secret_alerts[signing_secret_alert_key]}</Notification>;
    $[127] = signing_secret_alert_key;
    $[128] = signing_secret_alerts;
    $[129] = t41;
  } else {
    t41 = $[129];
  }
  let t42;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      align: "center",
      gap: 1
    };
    $[130] = t42;
  } else {
    t42 = $[130];
  }
  let t43;
  if ($[131] !== signing_secret_section.link.text) {
    t43 = <Text semi={true} muted={true}>{signing_secret_section.link.text}</Text>;
    $[131] = signing_secret_section.link.text;
    $[132] = t43;
  } else {
    t43 = $[132];
  }
  let t44;
  if ($[133] !== signing_secret_section.link.href || $[134] !== t43) {
    t44 = <Link neutral={true} href={signing_secret_section.link.href} target="_blank">{t43}</Link>;
    $[133] = signing_secret_section.link.href;
    $[134] = t43;
    $[135] = t44;
  } else {
    t44 = $[135];
  }
  let t45;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Text muted={true}><Icon icon="link" /></Text>;
    $[136] = t45;
  } else {
    t45 = $[136];
  }
  let t46;
  if ($[137] !== t44) {
    t46 = <Div flex={t42}>{t44}{t45}</Div>;
    $[137] = t44;
    $[138] = t46;
  } else {
    t46 = $[138];
  }
  let t47;
  if ($[139] !== t36 || $[140] !== t40 || $[141] !== t41 || $[142] !== t46) {
    t47 = <Container large={true} left={true} m={t31}><Div m={t32}>{t36}{t40}{t41}{t46}</Div></Container>;
    $[139] = t36;
    $[140] = t40;
    $[141] = t41;
    $[142] = t46;
    $[143] = t47;
  } else {
    t47 = $[143];
  }
  let t48;
  if ($[144] !== t30 || $[145] !== t47) {
    t48 = <>{t30}{t47}</>;
    $[144] = t30;
    $[145] = t47;
    $[146] = t48;
  } else {
    t48 = $[146];
  }
  return t48;
};
export default TeamApiKeys;
function _temp2(error) {
  return !isUnauthorizedError(error);
}
function _temp3(error_0) {
  return !isUnauthorizedError(error_0);
}
function _temp4(error_1) {
  return !isUnauthorizedError(error_1);
}
function _temp5() {
  return null;
}
function _temp6(loc) {
  return {
    ...loc,
    pathname: "/settings/project/secrets/add",
    state: {
      scroll: false
    }
  };
}
function _temp7(prev_0) {
  return !prev_0;
}
function _temp8(opened, toggle) {
  return <Button icon="horizontal_more" large={true} outline={true} active={opened} onClick={() => toggle(!opened)} />;
}